import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import StatusBadge from '../../components/UI/StatusBadge';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import dataService from '../../services/dataService';
import { generateTeachersListPDF } from '../../utils/pdfExport';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';

const { FiPlus, FiEdit2, FiTrash2, FiDownload, FiUpload, FiRefreshCw, FiFilter, FiMessageSquare, FiLogOut, FiLink, FiCheck, FiX } = FiIcons;

const AdminDashboard = () => {
  console.log("AdminDashboard - Component rendering");
  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('');
  const [minScore, setMinScore] = useState('');
  const [maxScore, setMaxScore] = useState('');
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { logout } = useAuth();

  // Load teachers data when component mounts
  useEffect(() => {
    console.log("AdminDashboard - useEffect running to load teachers");
    loadTeachers();
  }, []);

  // Apply filters when any filter or teachers data changes
  useEffect(() => {
    filterTeachers();
  }, [teachers, searchTerm, statusFilter, assignmentFilter, minScore, maxScore]);

  const loadTeachers = async () => {
    console.log("AdminDashboard - loadTeachers started");
    setLoading(true);
    try {
      // Try to get teachers from the dataService (which tries Supabase first)
      const teachersData = await dataService.getAllTeachers();
      console.log("AdminDashboard - teachers loaded:", teachersData.length);
      setTeachers(teachersData || []);
    } catch (error) {
      console.error('Error loading teachers:', error);
      // Fallback to localStorage if there's an error
      const localTeachers = JSON.parse(localStorage.getItem('teachers') || '[]');
      console.log("AdminDashboard - falling back to localStorage, teachers:", localTeachers.length);
      setTeachers(localTeachers.map(teacher => dataService.calculateScores(teacher)));
    } finally {
      setLoading(false);
    }
  };

  const filterTeachers = () => {
    if (!Array.isArray(teachers)) {
      console.error("Teachers is not an array:", teachers);
      setFilteredTeachers([]);
      return;
    }
    
    let filtered = [...teachers];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(teacher => 
        (teacher.full_name && teacher.full_name.toLowerCase().includes(searchTerm.toLowerCase())) || 
        (teacher.reg_no && teacher.reg_no.toLowerCase().includes(searchTerm.toLowerCase())) || 
        (teacher.school && teacher.school.toLowerCase().includes(searchTerm.toLowerCase())) || 
        (teacher.city && teacher.city.toLowerCase().includes(searchTerm.toLowerCase())) || 
        (teacher.governorate && teacher.governorate.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(teacher => teacher.status_code === statusFilter);
    }

    // Assignment completion filter
    if (assignmentFilter) {
      if (assignmentFilter === 'completed') {
        filtered = filtered.filter(teacher => teacher.assignments_completed !== false);
      } else if (assignmentFilter === 'incomplete') {
        filtered = filtered.filter(teacher => teacher.assignments_completed === false);
      }
    }

    // Score range filter
    if (minScore !== '') {
      filtered = filtered.filter(teacher => teacher.total_pct >= parseInt(minScore));
    }
    if (maxScore !== '') {
      filtered = filtered.filter(teacher => teacher.total_pct <= parseInt(maxScore));
    }

    setFilteredTeachers(filtered);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        // Delete from localStorage via dataService
        dataService.deleteTeacher(id);
        
        // Also try to delete from Supabase directly
        await supabase
          .from('teachers_x7a9b3c4d2')
          .delete()
          .eq('id', id);
          
        // Reload teachers
        loadTeachers();
      } catch (error) {
        console.error('Error deleting teacher:', error);
        // If there's an error with Supabase, still refresh local data
        loadTeachers();
      }
    }
  };

  const handleRecalculateAll = () => {
    dataService.recalculateAll();
    loadTeachers();
  };

  const handleExportCSV = () => {
    const csvContent = dataService.exportToCSV();
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'teachers_export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    if (!Array.isArray(filteredTeachers) || filteredTeachers.length === 0) {
      console.error("No teachers to export to PDF");
      return;
    }
    
    const doc = generateTeachersListPDF(filteredTeachers, t);
    doc.save('teachers_report.pdf');
  };

  const handleImportCSV = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportLoading(true);
      setImportError(null);
      setImportSuccess(false);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          console.log("File read complete, content length:", e.target.result.length);
          const importedTeachers = dataService.importFromCSV(e.target.result);
          console.log(`Imported ${importedTeachers.length} teachers`);
          loadTeachers();
          setImportSuccess(true);
          // Clear success message after 3 seconds
          setTimeout(() => {
            setImportSuccess(false);
          }, 3000);
        } catch (error) {
          console.error("CSV import error:", error);
          setImportError(error.message || 'Error importing CSV');
        } finally {
          setImportLoading(false);
          // Reset the file input
          event.target.value = '';
        }
      };
      reader.onerror = (e) => {
        console.error("File reading error:", e);
        setImportError('Error reading the file');
        setImportLoading(false);
        // Reset the file input
        event.target.value = '';
      };
      reader.readAsText(file);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Function to get governorate label
  const getGovernorateName = (governorateValue) => {
    if (!governorateValue) return '-';
    return t(`governorates.${governorateValue}`);
  };

  console.log("AdminDashboard - rendering with loading:", loading);

  if (loading) {
    return (
      <Layout title={t('adminDashboard')}>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-minecraft-green mx-auto mb-4"></div>
            <p className="text-gray-600">{t('loading')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={t('adminDashboard')}>
      <div className="space-y-6">
        {/* Header Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">{t('teacherRecords')}</h1>
              <Button onClick={handleLogout} variant="secondary" size="sm">
                <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                <span>{t('logout')}</span>
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate('/admin/teacher')} variant="primary">
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>{t('createNew')}</span>
              </Button>
              <Button onClick={() => navigate('/admin/messages')} variant="primary">
                <SafeIcon icon={FiMessageSquare} className="w-4 h-4" />
                <span>{t('messageManagement')}</span>
              </Button>
              <Button onClick={() => navigate('/admin/links')} variant="primary">
                <SafeIcon icon={FiLink} className="w-4 h-4" />
                <span>{t('linkManagement')}</span>
              </Button>
              <Button onClick={handleRecalculateAll} variant="secondary">
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                <span>{t('recalculateAll')}</span>
              </Button>
              <Button onClick={handleExportCSV} variant="secondary">
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                <span>{t('exportCSV')}</span>
              </Button>
              <Button onClick={handleExportPDF} variant="secondary">
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                <span>{t('exportPDF')}</span>
              </Button>
              <label className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg cursor-pointer transition-colors">
                <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
                <span>{importLoading ? t('loading') : t('importCSV')}</span>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                  className="hidden"
                  disabled={importLoading}
                />
              </label>
            </div>
          </div>

          {/* Import Status Messages */}
          {importError && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-800 p-3 rounded-md">
              {importError}
            </div>
          )}
          {importSuccess && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-800 p-3 rounded-md">
              {t('dataImported')}
            </div>
          )}
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <Input
              placeholder={t('search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-minecraft-green"
            >
              <option value="">{t('allStatuses')}</option>
              <option value="TOP_DISTINGUISHED">{t('topDistinguished')}</option>
              <option value="PASSED">{t('passed')}</option>
              <option value="FAIL">{t('didNotPass')}</option>
            </select>
            <select
              value={assignmentFilter}
              onChange={(e) => setAssignmentFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-minecraft-green"
            >
              <option value="">{t('allAssignments')}</option>
              <option value="completed">{t('assignmentsComplete')}</option>
              <option value="incomplete">{t('assignmentsIncomplete')}</option>
            </select>
            <Input
              type="number"
              placeholder={t('minScore')}
              value={minScore}
              onChange={(e) => setMinScore(e.target.value)}
              min="0"
              max="100"
            />
            <Input
              type="number"
              placeholder={t('maxScore')}
              value={maxScore}
              onChange={(e) => setMaxScore(e.target.value)}
              min="0"
              max="100"
            />
            <Button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setAssignmentFilter('');
                setMinScore('');
                setMaxScore('');
              }}
              variant="secondary"
            >
              <SafeIcon icon={FiFilter} className="w-4 h-4" />
              <span>{t('clearFilters')}</span>
            </Button>
          </div>
        </motion.div>

        {/* Teachers Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('registrationNumber')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fullName')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('school')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('governorate')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('totalPercentage')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('assignments')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('statusCode')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('lastUpdated')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(filteredTeachers) && filteredTeachers.map((teacher, index) => (
                  <motion.tr
                    key={teacher.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {teacher.reg_no}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {teacher.full_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {teacher.school || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getGovernorateName(teacher.governorate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="font-semibold">{teacher.total_pct}%</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {teacher.assignments_completed !== false ? (
                        <span className="inline-flex items-center text-green-600">
                          <SafeIcon icon={FiCheck} className="w-4 h-4 mr-1" />
                          {t('completed')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-orange-600">
                          <SafeIcon icon={FiX} className="w-4 h-4 mr-1" />
                          {t('incomplete')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={teacher.status_code} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {teacher.last_updated && format(new Date(teacher.last_updated), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/admin/teacher/${teacher.id}`)}
                          className="text-minecraft-blue hover:text-blue-600"
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(teacher.id)}
                          className="text-minecraft-red hover:text-red-600"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!Array.isArray(filteredTeachers) || filteredTeachers.length === 0) && (
            <div className="text-center py-12">
              <p className="text-gray-500">{t('noResults')}</p>
            </div>
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;